import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
    GenerateGroupImagesDetailResponse,
    GenerateGroupItemImageDetailResponse,
    GenerationStatus,
    PageGenerateGroupImagesDetailResponse,
} from '@/common/api/client/types.gen'
import { updateGenerationGroupApiV1GenerationsOrgIdGenerationGroupsGenerationGroupIdPut } from '@/common/api/client/sdk.gen'
import { resolveRelativeAssetUrl } from '@/common/lib/assetsUrl'
import { cn } from '@/common/lib/cn/cn'
import { ChoiceGroup } from '@/common/ui/ChoiceGroup'
import { CollapsibleCard, CollapsibleCardGroup, useCollapsibleCardGroup } from '@/common/ui/CollapsibleCard'
import { createDownloadZip } from '@/common/ui/DownloadZip'
import { Button } from '@/common/ui/Form/Button'
import { InlineEdit, Textarea } from '@/common/ui/Form'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Icon } from '@/common/ui/Icon'
import { MediaGallery } from '@/common/ui/MediaGallery'
import { Pagination } from '@/common/ui/Pagination'
import { TokenAmount } from '@/common/ui/TokenAmount'
import { useProductMediaImageModifyForm, type ProductMediaImageGroupsState } from '../../model'
import { detailQueryKey, useProductFlowListRowDetailQuery } from '../../model/productFlow'
import {
    formatGroupGenerationItemDisplayType,
    type GroupGenerationItemWithOverlay,
} from '../../lib/formatGroupGenerationItemLabels'
import { formatDate } from '@/common/lib/dateUtils'

const HEADER_THUMB_LIMIT = 5

type MediaItem = {
    type: 'image'
    url: string | null
    originalIndex: number
    status?: 'failed' | 'completed' | 'pending' | 'running' | 'cancelled'
    displayGenerationType?: string
    imageName?: string | null
    textOnImage?: string | null
}

const sortGenerations = (generations: GenerateGroupItemImageDetailResponse[] | undefined) => {
    if (!generations?.length) {
        return []
    }
    return [...generations].sort((a, b) => (a.group_order ?? 0) - (b.group_order ?? 0))
}

const mapGenerationStatus = (status: GenerationStatus): MediaItem['status'] | undefined => {
    if (status === 'failed') {
        return 'failed'
    }
    if (status === 'completed') {
        return 'completed'
    }
    if (status === 'pending' || status === 'created') {
        return 'pending'
    }
    if (status === 'running') {
        return 'running'
    }
    if (status === 'cancelled') {
        return 'cancelled'
    }
    return undefined
}

const generationsToMediaItems = (generations: GenerateGroupItemImageDetailResponse[] | undefined): MediaItem[] => {
    const sorted = sortGenerations(generations)
    return sorted.map((g, index) => {
        const withOverlay = g as GroupGenerationItemWithOverlay
        return {
            type: 'image' as const,
            url: g.url ? (resolveRelativeAssetUrl(g.url) ?? null) : null,
            originalIndex: index,
            status: mapGenerationStatus(g.status),
            displayGenerationType: formatGroupGenerationItemDisplayType(g),
            imageName: g.image_name ?? null,
            textOnImage: withOverlay.text_on_image ?? null,
        }
    })
}

const getHeaderThumbUrls = (generations: GenerateGroupItemImageDetailResponse[] | undefined): string[] => {
    const sorted = sortGenerations(generations)
    const out: string[] = []
    for (let i = 0; i < sorted.length; i++) {
        const raw = sorted[i]?.url
        if (!raw) {
            continue
        }
        const resolved = resolveRelativeAssetUrl(raw)
        if (resolved) {
            out.push(resolved)
        }
    }
    return out
}

const GroupHeaderThumbnails = ({ urls }: { urls: string[] }) => {
    if (urls.length === 0) {
        return null
    }

    const visibleUrls = urls.slice(0, HEADER_THUMB_LIMIT)
    const hiddenCount = urls.length - visibleUrls.length
    const narrowHiddenCount = urls.length - 1

    return (
        <div className='flex gap-1 items-center' aria-hidden>
            {visibleUrls.map((src, index) => (
                <img
                    key={`${src}-${index}`}
                    src={src}
                    alt=''
                    className={cn(
                        'h-8 w-8 shrink-0 rounded-md object-cover border border-white/10',
                        index > 0 && 'max-sm:hidden'
                    )}
                />
            ))}
            {hiddenCount > 0 && (
                <div className='h-8 w-8 shrink-0 rounded-md border border-white/10 bg-white/5 flex items-center justify-center text-[11px] font-medium text-white/80 max-sm:hidden'>
                    +{hiddenCount}
                </div>
            )}
            {narrowHiddenCount > 0 && (
                <div className='h-8 w-8 shrink-0 rounded-md border border-white/10 bg-white/5 hidden max-sm:flex items-center justify-center text-[11px] font-medium text-white/80'>
                    +{narrowHiddenCount}
                </div>
            )}
        </div>
    )
}

type MediaGroupCollapsibleProps = {
    group: GenerateGroupImagesDetailResponse
    state: ProductMediaImageGroupsState
    disableAnimation?: boolean
}

const MediaGroupCollapsible = ({ group, state, disableAnimation = false }: MediaGroupCollapsibleProps) => {
    const collapsibleGroup = useCollapsibleCardGroup()
    const queryClient = useQueryClient()
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
    const [isDownloadingAll, setIsDownloadingAll] = useState(false)
    const [groupName, setGroupName] = useState(group.name ?? '')
    const [pendingSelectedGenerationId, setPendingSelectedGenerationId] = useState<string | null>(null)
    const { orgId, productId, flowKey, fetchDetail, terminalStatuses, pollIntervalMs } = state
    const isOpen = collapsibleGroup?.openItemId === group.id

    useEffect(() => {
        setGroupName(group.name ?? '')
    }, [group.name])

    const rowDetailQuery = useProductFlowListRowDetailQuery<GenerateGroupImagesDetailResponse>({
        flowKey,
        orgId,
        resourceId: group.id,
        fetchDetail,
        terminalStatuses,
        pollIntervalMs,
        enabled: isOpen,
    })

    const generationsSource = rowDetailQuery.data?.generations ?? group.generations
    const sortedGenerations = useMemo(() => sortGenerations(generationsSource), [generationsSource])
    const allMedia = useMemo(() => generationsToMediaItems(sortedGenerations), [sortedGenerations])
    const galleryImages = useMemo(() => {
        return allMedia.filter((item): item is MediaItem & { url: string } => Boolean(item.url))
    }, [allMedia])
    const selectedMedia =
        selectedMediaIndex >= 0 && selectedMediaIndex < allMedia.length ? allMedia[selectedMediaIndex] : undefined
    const selectedGeneration =
        selectedMediaIndex >= 0 && selectedMediaIndex < sortedGenerations.length
            ? sortedGenerations[selectedMediaIndex]
            : undefined

    useEffect(() => {
        if (allMedia.length === 0) {
            setSelectedMediaIndex(0)
            return
        }
        if (selectedMediaIndex >= allMedia.length) {
            setSelectedMediaIndex(allMedia.length - 1)
        }
    }, [allMedia.length, selectedMediaIndex])

    useEffect(() => {
        if (!pendingSelectedGenerationId) {
            return
        }

        const nextSelectedIndex = sortedGenerations.findIndex(
            generation => generation.id === pendingSelectedGenerationId
        )
        if (nextSelectedIndex < 0) {
            return
        }

        setSelectedMediaIndex(nextSelectedIndex)
        setPendingSelectedGenerationId(null)
    }, [pendingSelectedGenerationId, sortedGenerations])

    const headerThumbUrls = useMemo(() => getHeaderThumbUrls(group.generations), [group.generations])

    const fallbackGroupTitle = `Группа ${group.id.slice(0, 8)}…`

    const updateGroupNameMutation = useMutation({
        mutationFn: async (nextName: string) => {
            if (!orgId) {
                throw new Error('Организация не выбрана')
            }

            const response = await updateGenerationGroupApiV1GenerationsOrgIdGenerationGroupsGenerationGroupIdPut({
                path: {
                    org_id: orgId,
                    generation_group_id: group.id,
                },
                body: {
                    name: nextName || null,
                },
            })

            return response.data
        },
        onSuccess: () => {
            if (!orgId) {
                return
            }

            void queryClient.invalidateQueries({
                queryKey: ['product-flow', flowKey, 'list', orgId, productId],
            })
            void queryClient.invalidateQueries({
                queryKey: detailQueryKey(flowKey, orgId, group.id),
            })
        },
        onError: () => {
            setGroupName(group.name ?? '')
        },
    })

    const modifyImageForm = useProductMediaImageModifyForm({
        orgId,
        productId,
        flowKey,
        groupId: group.id,
        selectedGeneration,
        detailGenerations: rowDetailQuery.data?.generations,
        onSubmittedGenerationId: setPendingSelectedGenerationId,
    })

    const handleSaveGroupName = (nextValue: string) => {
        if (nextValue === (group.name ?? '')) {
            setGroupName(nextValue)
            return
        }

        setGroupName(nextValue)
        updateGroupNameMutation.mutate(nextValue)
    }

    const handleMediaSelect = (index: number) => {
        setSelectedMediaIndex(index)
    }

    const handleDownloadAll = async () => {
        if (isDownloadingAll || galleryImages.length === 0) {
            return
        }

        setIsDownloadingAll(true)
        try {
            const binaryFiles = await Promise.all(
                galleryImages.map(async (image, index) => {
                    const response = await fetch(image.url)
                    if (!response.ok) {
                        throw new Error('Не удалось загрузить файлы для архива')
                    }

                    const blob = await response.blob()
                    const extension = blob.type.split('/')[1] || 'jpg'
                    return {
                        name: `image-${index + 1}.${extension}`,
                        content: blob,
                    }
                })
            )

            const archive = await createDownloadZip({
                archiveName: `media-${group.id || 'archive'}.zip`,
                binaryFiles,
            })

            archive.download()
            setTimeout(archive.revoke, 0)
        } catch (error) {
            console.error('Ошибка при скачивании всех файлов:', error)
        } finally {
            setIsDownloadingAll(false)
        }
    }

    const showLoading = isOpen && rowDetailQuery.isLoading && !rowDetailQuery.data && !group.generations?.length
    const imagePromptTextareaId = `modify-image-prompt-${group.id}-${selectedGeneration?.id ?? 'empty'}`
    const userPromptTextareaId = `modify-image-text-${group.id}-${selectedGeneration?.id ?? 'empty'}`
    const regenerateCostTokens = selectedGeneration?.regenerate_cost_tokens ?? 0
    const isSelectedGenerationFailed = selectedGeneration?.status === 'failed'
    const hasSelectedGenerationReferenceFileUrl = Boolean(
        selectedGeneration?.reference_files?.some(referenceFile => referenceFile.url.trim())
    )

    return (
        <CollapsibleCard
            type='result'
            title={
                (
                    <InlineEdit
                        value={groupName}
                        placeholder={fallbackGroupTitle}
                        onSave={handleSaveGroupName}
                        disabled={updateGroupNameMutation.isPending}
                        saveOnBlur
                        className='max-w-full ml-[-9px]'
                    />
                ) as React.ReactNode
            }
            headerPreview={<GroupHeaderThumbnails urls={headerThumbUrls} />}
            groupItemId={group.id}
            ariaLabel={`Группа генерации ${group.name || group.id}`}
            description={formatDate(group.created_at)}
            className='w-full min-w-0 max-w-full'
            disableAnimation={disableAnimation}
        >
            {showLoading && (
                <div className='text-sm text-[#aba7af]' role='status' aria-live='polite'>
                    Загрузка…
                </div>
            )}
            {rowDetailQuery.isError && (
                <div className='text-sm text-red-400' role='alert'>
                    Ошибка загрузки группы
                </div>
            )}
            {!showLoading && allMedia.length === 0 && (
                <div className='text-sm text-[#aba7af]' role='status'>
                    Пока нет изображений в этой группе
                </div>
            )}
            {group.status === 'failed' && allMedia.length === 0 && (
                <div className='text-sm text-[#aba7af]' role='status'>
                    Генерация не удалась
                </div>
            )}
            {allMedia.length > 0 && (
                <div className='flex flex-col gap-4 w-full min-w-0 max-w-full'>
                    <MediaGallery
                        className='w-full min-w-0 max-w-full'
                        allMedia={allMedia}
                        selectedMediaIndex={selectedMediaIndex}
                        onMediaSelect={handleMediaSelect}
                        miniaturesPosition='top'
                        detailsContent={
                            <div className='flex flex-col gap-3 flex-1 w-full items-stretch justify-start relative shrink-0'>
                                <div className='flex flex-col font-normal justify-center leading-[0] relative shrink-0 text-sm text-[var(--pixorion-gray-2)]'>
                                    <p className='leading-[1.4] text-nowrap'>
                                        {selectedMediaIndex >= 0 ? selectedMediaIndex + 1 : 0} / {allMedia.length}
                                    </p>
                                </div>
                                {isSelectedGenerationFailed ? (
                                    hasSelectedGenerationReferenceFileUrl && (
                                        <form
                                            className='flex flex-col gap-2 w-full min-w-0'
                                            onSubmit={modifyImageForm.handleRegenerateSubmit}
                                        >
                                            {modifyImageForm.selectedRegenerateErrorMessage && (
                                                <p className='text-sm leading-[1.4] text-red-400' role='alert'>
                                                    {modifyImageForm.selectedRegenerateErrorMessage}
                                                </p>
                                            )}
                                            <Button
                                                type='submit'
                                                size='md'
                                                className='self-start'
                                                loading={modifyImageForm.isSelectedRegeneratePending}
                                                disabled={!modifyImageForm.canSubmitRegenerate}
                                            >
                                                Перегенерировать
                                                {!modifyImageForm.isSelectedRegeneratePending &&
                                                    regenerateCostTokens > 0 && (
                                                        <TokenAmount value={regenerateCostTokens} />
                                                    )}
                                            </Button>
                                        </form>
                                    )
                                ) : (
                                    <>
                                        {selectedMedia?.displayGenerationType?.trim() && (
                                            <div className='flex flex-col gap-1 w-full min-w-0'>
                                                <span className='text-xs font-normal leading-[1.4] text-[var(--pixorion-gray-2)]'>
                                                    Тип
                                                </span>
                                                <p className='font-medium leading-[1.4] text-base text-white whitespace-normal break-words'>
                                                    {selectedMedia.displayGenerationType.trim()}{' '}
                                                    {selectedMedia?.imageName?.trim() && ' / '}
                                                    {selectedMedia?.imageName?.trim()}
                                                </p>
                                            </div>
                                        )}
                                        <div className='flex flex-col gap-3 w-full min-w-0'>
                                            <div className='flex flex-col gap-2 w-full min-w-0'>
                                                <div className='flex items-center gap-2 w-full min-w-0'>
                                                    {hasSelectedGenerationReferenceFileUrl && (
                                                        <form
                                                            className='shrink-0'
                                                            onSubmit={modifyImageForm.handleRegenerateSubmit}
                                                        >
                                                            <Button
                                                                type='submit'
                                                                size='md'
                                                                variant='ghost'
                                                                className='self-start'
                                                                loading={modifyImageForm.isSelectedRegeneratePending}
                                                                disabled={!modifyImageForm.canSubmitRegenerate}
                                                                title='Перегенерировать'
                                                            >
                                                                <Icon name='refresh' size='md' />
                                                            </Button>
                                                        </form>
                                                    )}
                                                    <ChoiceGroup
                                                        className='w-full min-w-0'
                                                        items={['Изменить текст', 'Изменить картинку']}
                                                        activeIndex={
                                                            modifyImageForm.activeModifyMode === 'image' ? 1 : 0
                                                        }
                                                        onChange={modifyImageForm.handleModifyModeChange}
                                                    />
                                                </div>
                                                {hasSelectedGenerationReferenceFileUrl &&
                                                    modifyImageForm.selectedRegenerateErrorMessage && (
                                                        <p className='text-sm leading-[1.4] text-red-400' role='alert'>
                                                            {modifyImageForm.selectedRegenerateErrorMessage}
                                                        </p>
                                                    )}
                                            </div>
                                            {modifyImageForm.activeModifyMode === 'text' ? (
                                                <form
                                                    className='flex flex-col gap-2 w-full min-w-0'
                                                    onSubmit={modifyImageForm.handleModifyTextSubmit}
                                                >
                                                    <label
                                                        htmlFor={userPromptTextareaId}
                                                        className='text-xs font-normal leading-[1.4] text-[var(--pixorion-gray-2)]'
                                                    >
                                                        Изменить текст / промпт
                                                    </label>
                                                    <Fieldset
                                                        errorText={modifyImageForm.selectedModifyTextErrorMessage}
                                                        required
                                                    >
                                                        <Textarea
                                                            id={userPromptTextareaId}
                                                            name='text_user_prompt'
                                                            value={modifyImageForm.textUserPrompt}
                                                            rows={4}
                                                            size='md'
                                                            aria-label='Промпт для изменения текста на изображении'
                                                            placeholder='Опишите, какой текст нужно получить на изображении'
                                                            disabled={
                                                                modifyImageForm.isSelectedModifyTextPending ||
                                                                !selectedGeneration?.asset_id
                                                            }
                                                            className='w-full min-h-[88px] resize-none'
                                                            onChange={modifyImageForm.handleTextUserPromptChange}
                                                        />
                                                    </Fieldset>
                                                    <Button
                                                        type='submit'
                                                        size='md'
                                                        className='self-start'
                                                        loading={modifyImageForm.isSelectedModifyTextPending}
                                                        disabled={!modifyImageForm.canSubmitModifyText}
                                                    >
                                                        Изменить текст
                                                        {!modifyImageForm.isSelectedModifyTextPending &&
                                                            regenerateCostTokens > 0 && (
                                                                <TokenAmount value={regenerateCostTokens} />
                                                            )}
                                                    </Button>
                                                </form>
                                            ) : (
                                                <form
                                                    className='flex flex-col gap-2 w-full min-w-0'
                                                    onSubmit={modifyImageForm.handleModifyImageSubmit}
                                                >
                                                    <label
                                                        htmlFor={imagePromptTextareaId}
                                                        className='text-xs font-normal leading-[1.4] text-[var(--pixorion-gray-2)]'
                                                    >
                                                        Изменить картинку / промпт
                                                    </label>
                                                    <Fieldset
                                                        errorText={modifyImageForm.selectedModifyImageErrorMessage}
                                                        required
                                                    >
                                                        <Textarea
                                                            id={imagePromptTextareaId}
                                                            name='image_user_prompt'
                                                            value={modifyImageForm.imageUserPrompt}
                                                            rows={4}
                                                            size='md'
                                                            aria-label='Промпт для изменения картинки'
                                                            placeholder='Опишите, как нужно изменить картинку'
                                                            disabled={
                                                                modifyImageForm.isSelectedModifyImagePending ||
                                                                !selectedGeneration?.asset_id
                                                            }
                                                            className='w-full min-h-[88px] resize-none'
                                                            onChange={modifyImageForm.handleImageUserPromptChange}
                                                        />
                                                    </Fieldset>
                                                    <Button
                                                        type='submit'
                                                        size='md'
                                                        className='self-start'
                                                        loading={modifyImageForm.isSelectedModifyImagePending}
                                                        disabled={!modifyImageForm.canSubmitModifyImage}
                                                    >
                                                        Изменить картинку
                                                        {!modifyImageForm.isSelectedModifyImagePending &&
                                                            regenerateCostTokens > 0 && (
                                                                <TokenAmount value={regenerateCostTokens} />
                                                            )}
                                                    </Button>
                                                </form>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        }
                    />
                    <div className='flex justify-end w-full'>
                        <Button
                            type='button'
                            variant='outline'
                            size='lg'
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.preventDefault()
                                void handleDownloadAll()
                            }}
                            loading={isDownloadingAll}
                            disabled={isDownloadingAll || galleryImages.length === 0}
                        >
                            <Icon name='export' className='w-5 h-5' />
                            Скачать всё
                        </Button>
                    </div>
                </div>
            )}
        </CollapsibleCard>
    )
}

type MediaImageGroupsListItemsProps = {
    listItems: GenerateGroupImagesDetailResponse[]
    state: ProductMediaImageGroupsState
    shouldTrackNewGroup: boolean
    onNewGroupHandled: () => void
}

const MediaImageGroupsListItems = ({
    listItems,
    state,
    shouldTrackNewGroup,
    onNewGroupHandled,
}: MediaImageGroupsListItemsProps) => {
    const collapsibleGroup = useCollapsibleCardGroup()
    const prevItemIdsRef = useRef<string[]>([])
    const [disableAnimationItemId, setDisableAnimationItemId] = useState<string | null>(null)

    useLayoutEffect(() => {
        const itemIds = listItems.map(item => item.id).filter(Boolean)
        const openItemExists = itemIds.includes(collapsibleGroup?.openItemId ?? '')
        const hasInteracted = collapsibleGroup?.hasInteracted ?? false

        if (!itemIds.length) {
            prevItemIdsRef.current = itemIds
            return
        }

        if (shouldTrackNewGroup) {
            const prevItemIds = prevItemIdsRef.current
            const newItemId = itemIds.find(id => !prevItemIds.includes(id))

            if (newItemId) {
                collapsibleGroup?.setHasInteracted(false)
                collapsibleGroup?.setOpenItemId(newItemId)
                requestAnimationFrame(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                })
                onNewGroupHandled()
                prevItemIdsRef.current = itemIds
                return
            }
        }

        if (!openItemExists && !hasInteracted) {
            const firstItemId = itemIds[0] ?? null
            setDisableAnimationItemId(firstItemId)
            collapsibleGroup?.setOpenItemId(firstItemId)
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setDisableAnimationItemId(null)
                })
            })
        }

        prevItemIdsRef.current = itemIds
    }, [collapsibleGroup, listItems, onNewGroupHandled, shouldTrackNewGroup])

    return (
        <ul className='flex flex-col gap-[12px] w-full min-w-0 max-w-full list-none p-0 m-0' role='list'>
            {listItems.map(item => {
                if (!item.id) {
                    return null
                }

                return (
                    <li key={item.id} role='listitem'>
                        <MediaGroupCollapsible
                            group={item}
                            state={state}
                            disableAnimation={disableAnimationItemId === item.id}
                        />
                    </li>
                )
            })}
        </ul>
    )
}

type PageListLike = {
    items?: Array<{ id?: string }>
    pages?: number | null
}

type MediaImageGroupsListProps = {
    state: ProductMediaImageGroupsState
    emptyListMessage?: string
}

export const MediaImageGroupsList = ({
    state,
    emptyListMessage = 'Нет групп для этого товара. Запустите генерацию.',
}: MediaImageGroupsListProps) => {
    const { listQuery, orgId, productId, listCurrentPage, handleListPageChange, generationMutation } = state

    const [shouldTrackNewGroup, setShouldTrackNewGroup] = useState(false)

    useEffect(() => {
        if (generationMutation.isError) {
            setShouldTrackNewGroup(false)
            return
        }

        if (generationMutation.isPending) {
            setShouldTrackNewGroup(true)
        }
    }, [generationMutation.isError, generationMutation.isPending])

    const listPayload = listQuery.data ?? null
    const listItems = (listPayload as PageGenerateGroupImagesDetailResponse | null)?.items ?? []
    const totalPages = Math.max(1, (listPayload as PageListLike | null)?.pages ?? 1)

    if (!orgId || !productId) {
        return null
    }

    if (listQuery.isLoading) {
        return (
            <div className='text-sm text-[#aba7af]' role='status' aria-live='polite'>
                Загрузка списка…
            </div>
        )
    }

    if (listQuery.isError) {
        return (
            <div className='text-sm text-red-400' role='alert'>
                Ошибка загрузки списка
            </div>
        )
    }

    return (
        <section
            aria-label='Список групп генерации по товару'
            className='flex flex-col gap-3 w-full min-w-0 max-w-full'
        >
            {listItems.length === 0 && (
                <div className='text-sm text-[#aba7af] hidden md:block' role='status'>
                    {emptyListMessage}
                </div>
            )}
            {listItems.length > 0 && (
                <CollapsibleCardGroup>
                    <MediaImageGroupsListItems
                        listItems={listItems}
                        state={state}
                        shouldTrackNewGroup={shouldTrackNewGroup}
                        onNewGroupHandled={() => setShouldTrackNewGroup(false)}
                    />
                </CollapsibleCardGroup>
            )}
            <Pagination
                className='mt-1 w-full justify-center'
                currentPage={listCurrentPage}
                totalPages={totalPages}
                onPageChange={handleListPageChange}
            />
        </section>
    )
}
